import { createContext, useContext, useEffect, useState } from "react";
import {
  doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot,
} from "firebase/firestore";
import { deleteDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../config/firebase";

const BudgetContext = createContext();
export const useBudget = () => useContext(BudgetContext);

const getBudgetRef = (uid, workspaceId) =>
  workspaceId
    ? doc(db, "workspaces", workspaceId, "budget", "data")
    : doc(db, "users", uid, "budget", "data");

const DEFAULT_DATA = {
  monthlyBudget: 0,
  categories: [
    { id: 1, name: "Payroll",         limit: 0 },
    { id: 2, name: "Infrastructure",  limit: 0 },
    { id: 3, name: "Marketing",       limit: 0 },
    { id: 4, name: "Miscellaneous",   limit: 0 },
  ],
  transactions: [],
  recurringTransactions: [],
  runwaySettings: {
    cashBalance: "", monthlyBurnOverride: "", mrr: "",
    monthsToClose: "6", targetPostRaiseRunway: "18", momGrowthRate: "", mrrHistory: {},
  },
  departments: [
    { id: 1, name: "Engineering", budget: 0, headcount: 0, colorIdx: 0 },
    { id: 2, name: "Marketing",   budget: 0, headcount: 0, colorIdx: 1 },
    { id: 3, name: "Sales",       budget: 0, headcount: 0, colorIdx: 2 },
    { id: 4, name: "Operations",  budget: 0, headcount: 0, colorIdx: 3 },
  ],
  groups: [],
  shareEnabled: false,
  shareToken: null,
};

export function BudgetProvider({ children }) {
  const [uid, setUid]                                     = useState(null);
  const [workspaceId, setWorkspaceId]                     = useState(null);
  const [userRole, setUserRole]                           = useState("admin");
  const [monthlyBudget, setMonthlyBudgetState]            = useState(0);
  const [categories, setCategories]                       = useState(DEFAULT_DATA.categories);
  const [transactions, setTransactions]                   = useState([]);
  const [recurringTransactions, setRecurringTransactions] = useState([]);
  const [runwaySettings, setRunwaySettings]               = useState(DEFAULT_DATA.runwaySettings);
  const [departments, setDepartments]                     = useState(DEFAULT_DATA.departments);
  const [groups, setGroups]                               = useState(DEFAULT_DATA.groups);
  const [shareEnabled, setShareEnabled]                   = useState(false);
  const [shareToken, setShareToken]                       = useState(null);
  const [budgetLoading, setBudgetLoading]                 = useState(true);
  const [viewerToast, setViewerToast]                     = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
      if (!user) { setWorkspaceId(null); setUserRole(null); }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(db, "users", uid), (snap) => {
      if (snap.exists()) {
        setWorkspaceId(snap.data().workspaceId ?? null);
        setUserRole(snap.data().role ?? "admin");
      } else {
        setWorkspaceId(null);
        setUserRole("admin");
      }
    });
    return unsub;
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setMonthlyBudgetState(0);
      setCategories(DEFAULT_DATA.categories);
      setTransactions([]);
      setRecurringTransactions([]);
      setBudgetLoading(false);
      return;
    }

    setBudgetLoading(true);
    const ref = getBudgetRef(uid, workspaceId);

    getDoc(ref).then((snap) => {
      if (!snap.exists()) setDoc(ref, DEFAULT_DATA);
    });

    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setMonthlyBudgetState(data.monthlyBudget ?? 0);
        setCategories(data.categories ?? DEFAULT_DATA.categories);
        setTransactions(data.transactions ?? []);
        setRunwaySettings(data.runwaySettings ?? DEFAULT_DATA.runwaySettings);
        setDepartments(data.departments ?? DEFAULT_DATA.departments);
        setGroups(data.groups ?? DEFAULT_DATA.groups);
        setShareEnabled(data.shareEnabled ?? false);
        setShareToken(data.shareToken ?? null);

        // Due recurring transactions are materialized server-side by the
        // scheduled processRecurringTransactions function (idempotent, no races).
        setRecurringTransactions(data.recurringTransactions ?? []);
      }
      setBudgetLoading(false);
    });

    return unsub;
  }, [uid, workspaceId]);

  const isViewer = userRole === "viewer";
  const getRef = () => getBudgetRef(uid, workspaceId);

  const blockViewer = () => {
    setViewerToast(true);
    setTimeout(() => setViewerToast(false), 2500);
  };

  const setMonthlyBudget = (value) => {
    if (isViewer) { blockViewer(); return; }
    return updateDoc(getRef(), { monthlyBudget: Number(value) });
  };

  const addCategory = (name) => {
    if (isViewer) { blockViewer(); return; }
    return updateDoc(getRef(), { categories: arrayUnion({ id: Date.now(), name, limit: 0 }) });
  };

  const deleteCategory = async (categoryId) => {
    if (isViewer) { blockViewer(); return; }
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return;
    const updatedTransactions = transactions.filter((tx) => tx.categoryId !== categoryId);
    return updateDoc(getRef(), { categories: arrayRemove(cat), transactions: updatedTransactions });
  };

  const updateCategoryLimit = (categoryId, newLimit) => {
    if (isViewer) { blockViewer(); return; }
    const updated = categories.map((cat) =>
      cat.id === categoryId ? { ...cat, limit: Number(newLimit) } : cat
    );
    return updateDoc(getRef(), { categories: updated });
  };

  const addTransaction = (tx) => {
    if (isViewer) { blockViewer(); return; }
    return updateDoc(getRef(), { transactions: arrayUnion({ ...tx, id: Date.now() }) });
  };

  const deleteTransaction = async (txId) => {
    if (isViewer) { blockViewer(); return; }
    const tx = transactions.find((t) => t.id === txId);
    if (!tx) return;
    return updateDoc(getRef(), { transactions: arrayRemove(tx) });
  };

  const addRecurring = (template) => {
    if (isViewer) { blockViewer(); return; }
    const newTemplate = { ...template, id: Date.now(), lastProcessed: null, active: true };
    return updateDoc(getRef(), { recurringTransactions: [...recurringTransactions, newTemplate] });
  };

  const deleteRecurring = (templateId) => {
    if (isViewer) { blockViewer(); return; }
    return updateDoc(getRef(), {
      recurringTransactions: recurringTransactions.filter(r => r.id !== templateId),
    });
  };

  const updateRunwaySettings = (patch) => {
    if (isViewer) { blockViewer(); return; }
    const merged = { ...runwaySettings, ...patch };
    const cash = Number(merged.cashBalance) || 0;
    const mrrNum = Number(merged.mrr) || 0;
    const tprNum = Math.max(Number(merged.targetPostRaiseRunway) || 18, 1);
    const smartBudget = cash > 0 ? Math.floor(cash / tprNum) + mrrNum : 0;
    const update = smartBudget > 0
      ? { runwaySettings: merged, monthlyBudget: smartBudget }
      : { runwaySettings: merged };
    return setDoc(getRef(), update, { merge: true })
      .catch(err => console.error("[greenline] updateRunwaySettings failed:", err));
  };

  const updateDepartments = (updated) => {
    if (isViewer) { blockViewer(); return; }
    return setDoc(getRef(), { departments: updated }, { merge: true })
      .catch(err => console.error("[greenline] updateDepartments failed:", err));
  };

  const updateGroups = (updated) => {
    if (isViewer) { blockViewer(); return; }
    return setDoc(getRef(), { groups: updated }, { merge: true })
      .catch(err => console.error("[greenline] updateGroups failed:", err));
  };

  const updateSharing = async (enabled) => {
    if (isViewer) { blockViewer(); return; }
    const ref = getRef();
    if (enabled) {
      const token = Array.from(
        crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, "0")
      ).join("");
      await setDoc(doc(db, "shares", token), { uid, workspaceId: workspaceId || null });
      if (shareToken) await deleteDoc(doc(db, "shares", shareToken)).catch(() => {});
      return updateDoc(ref, { shareEnabled: true, shareToken: token });
    } else {
      if (shareToken) await deleteDoc(doc(db, "shares", shareToken)).catch(() => {});
      return updateDoc(ref, { shareEnabled: false, shareToken: null });
    }
  };

  const toggleRecurring = (templateId) => {
    if (isViewer) { blockViewer(); return; }
    return updateDoc(getRef(), {
      recurringTransactions: recurringTransactions.map(r =>
        r.id === templateId ? { ...r, active: !r.active } : r
      ),
    });
  };

  const totalSpent = transactions.reduce((sum, tx) => sum + Number(tx.amount), 0);
  const remaining  = monthlyBudget - totalSpent;
  const getCategorySpending = (categoryId) =>
    transactions
      .filter((tx) => tx.categoryId === categoryId)
      .reduce((sum, tx) => sum + Number(tx.amount), 0);

  return (
    <BudgetContext.Provider value={{
      monthlyBudget, setMonthlyBudget,
      categories, addCategory, deleteCategory, updateCategoryLimit,
      transactions, addTransaction, deleteTransaction,
      recurringTransactions, addRecurring, deleteRecurring, toggleRecurring,
      runwaySettings, updateRunwaySettings,
      departments, updateDepartments,
      groups, updateGroups,
      shareEnabled, shareToken, updateSharing,
      totalSpent, remaining, getCategorySpending, budgetLoading,
      workspaceId, userRole, isViewer, viewerToast,
    }}>
      {children}
    </BudgetContext.Provider>
  );
}
